import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import NotificationStatusChip from '../messages/NotificationStatusChip';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Drawer from '@mui/material/Drawer';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/SaveAlt';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { trTR } from '@mui/x-date-pickers/locales';
import trLocale from 'date-fns/locale/tr';

import { fetchJson, getApiBase } from '../../apiBase';
import { formatSendStats } from './ExamCommunicationStatusPanel';
import { teacherTypography } from '../../design/typography';
import { developmentLevelLabel } from '../../shared/developmentLevels';
import StudentResultEntryCard from './StudentResultEntryCard';
import AssessmentScoringSelector from './AssessmentScoringSelector';
import CourseAssessmentList from './CourseAssessmentList';
import { unifiedUI } from '../../design/unified';

const api = () => {
  const base = `${getApiBase()}/admin`;
  return {
    list: (school) => `${base}/course-assessments/${encodeURIComponent(school)}`,
    create: (school) => `${base}/course-assessments/${encodeURIComponent(school)}`,
    detail: (school, examId) => `${base}/course-assessments/${encodeURIComponent(school)}/${encodeURIComponent(examId)}`,
    update: (school, examId) => `${base}/course-assessments/${encodeURIComponent(school)}/${encodeURIComponent(examId)}`,
    delete: (school, examId) => `${base}/course-assessments/${encodeURIComponent(school)}/${encodeURIComponent(examId)}`,
    sendPlan: (school, examId) => `${base}/course-assessments/${encodeURIComponent(school)}/${encodeURIComponent(examId)}/send-plan`,
    save: (school, examId) => `${base}/course-assessments/${encodeURIComponent(school)}/${encodeURIComponent(examId)}/results`,
    send: (school, examId) => `${base}/course-assessments/${encodeURIComponent(school)}/${encodeURIComponent(examId)}/send`,
    classes: (school) => `${base}/classes/${encodeURIComponent(school)}`,
    lessons: (school) => `${base}/lessons/${encodeURIComponent(school)}`,
  };
};

const humanDate = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleDateString('tr-TR');
  } catch (_) {
    return value;
  }
};

const RESULT_STATUS_ABSENT = 'absent';
const ABSENT_RESULT_TOKEN = '__absent__';

const hasScoreValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';
const normalizeResultStatus = (value) => (String(value || '').trim().toLowerCase() === RESULT_STATUS_ABSENT ? RESULT_STATUS_ABSENT : null);
const isAbsentDraftValue = (value) => String(value ?? '').trim() === ABSENT_RESULT_TOKEN;
const hasResultEntry = (value, resultStatus = null) => (
  normalizeResultStatus(resultStatus) === RESULT_STATUS_ABSENT || isAbsentDraftValue(value) || hasScoreValue(value)
);

const assessmentResultSection = (value, resultStatus = null) => {
  if (normalizeResultStatus(resultStatus) === RESULT_STATUS_ABSENT || isAbsentDraftValue(value)) return 'absent';
  return hasScoreValue(value) ? 'completed' : 'pending';
};

const resultEntryValidationError = (value, scoringMode, totalQuestions) => {
  const rawValue = String(value ?? '').trim();
  if (!rawValue || isAbsentDraftValue(rawValue)) return null;
  if (scoringMode === 'development') return developmentLevelLabel(rawValue) ? null : 'Geçerli bir gelişim düzeyi seçin.';

  const numericValue = Number(rawValue);
  if (Number.isFinite(numericValue) && numericValue < 0) {
    return 'Negatif değer girilemez.';
  }
  if (!Number.isInteger(numericValue)) {
    return 'Tam sayı girin.';
  }

  const maximum = scoringMode === 'test' ? Number(totalQuestions) : 100;
  if (!Number.isFinite(maximum) || maximum < 1) {
    return scoringMode === 'test' ? 'Toplam soru sayısı geçersiz.' : null;
  }
  if (numericValue > maximum) {
    return scoringMode === 'test'
      ? `Doğru sayısı ${maximum} soruyu geçemez.`
      : 'Puan 100’ü geçemez.';
  }
  return null;
};

const resultEntryValue = (row, scoringMode) => (
  scoringMode === 'development' ? row?.development_level ?? '' : scoringMode === 'test' ? row?.correct_count ?? '' : row?.result_value ?? ''
);

const resultEntryLabel = (scoringMode) => (
  scoringMode === 'development' ? 'Gelişim Düzeyi' : scoringMode === 'test' ? 'Doğru sayısı' : '100 üzerinden puan'
);

const toPickerDate = (value) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return null;
  const [year, month, day] = String(value).split('-').map(Number);
  const result = new Date(year, month - 1, day);
  return Number.isNaN(result.getTime()) ? null : result;
};

const toIsoDate = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const decorateExam = (exam) => {
  if (!exam) return exam;
  const suffixParts = [];
  if (exam.lesson_name) suffixParts.push(exam.lesson_name);
  if (exam.exam_date) suffixParts.push(humanDate(exam.exam_date));
  const suffix = suffixParts.length ? ` • ${suffixParts.join(' • ')}` : '';
  return {
    ...exam,
    label: `${exam.title || 'İsimsiz'}${suffix}`,
  };
};

function AssessmentActionMenu({ exam, onEdit, onDelete, disabled }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const assessmentTitle = exam?.title || 'Değerlendirme';

  const closeMenu = () => setAnchorEl(null);
  const openMenu = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const editAssessment = () => {
    closeMenu();
    onEdit(exam);
  };
  const deleteAssessment = () => {
    closeMenu();
    onDelete(exam);
  };

  return (
    <>
      <Tooltip title="Diğer işlemler">
        <span>
          <IconButton
            size="small"
            sx={{ minWidth: 44, minHeight: 44 }}
            aria-label={`${assessmentTitle} için diğer işlemler`}
            onClick={openMenu}
            disabled={disabled}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        MenuListProps={{ 'aria-label': `${assessmentTitle} işlemleri` }}
      >
        <MenuItem onClick={editAssessment}>Güncelle</MenuItem>
        <MenuItem onClick={deleteAssessment}>Sil</MenuItem>
      </Menu>
    </>
  );
}

export default function CourseAssessmentManager({ schoolId, onError, onSuccess, createRequestId = 0 }) {
  const apis = useMemo(() => api(), []);
  const scoreInputRefs = useRef({});
  const handledCreateRequest = useRef(0);
  const emptyCreateForm = {
    title: '', exam_date: '', class_id: null, grade_level: null, lesson_id: null,
    scoring_mode: 'manual', total_questions: '', notes: '', reminder_enabled: true,
  };
  const [exams, setExams] = useState([]);
  // Start in the loading state so the empty-state message is not shown before
  // the first assessment request has completed.
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState('');
  const [selectedExam, setSelectedExam] = useState(null);
  const [detail, setDetail] = useState({ rows: [], exam: null });
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [edits, setEdits] = useState({});
  const [resultErrors, setResultErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [sendingPlan, setSendingPlan] = useState(false);
  const [sending, setSending] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [classes, setClasses] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [createErrors, setCreateErrors] = useState({});
  const [createdExam, setCreatedExam] = useState(null);
  const [localAlert, setLocalAlert] = useState('');

  useEffect(() => {
    if (!createRequestId || handledCreateRequest.current === createRequestId) return;
    handledCreateRequest.current = createRequestId;
    setCreateForm({ title: '', exam_date: '', class_id: null, grade_level: null, lesson_id: null,
      scoring_mode: 'manual', total_questions: '', notes: '', reminder_enabled: true });
    setCreateErrors({});
    setCreatedExam(null);
    setShowCreate(true);
  }, [createRequestId]);

  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editExam, setEditExam] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '', exam_date: '', class_id: null, grade_level: null, lesson_id: null,
    scoring_mode: 'manual', total_questions: '', notes: '', reminder_enabled: true,
  });
  const [editErrors, setEditErrors] = useState({});
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteExam, setDeleteExam] = useState(null);

  const createClassId = createForm.class_id ?? (classes.length === 1 ? classes[0].id : null);
  const isLegacyGradeAssessment = Boolean(editExam?.grade_level);

  const selectedCreateClass = useMemo(() => {
    return classes.find((c) => String(c?.id) === String(createClassId)) || null;
  }, [classes, createClassId]);

  const selectedEditClass = useMemo(() => {
    if (!editForm.class_id) return null;
    return classes.find((c) => String(c?.id) === String(editForm.class_id)) || null;
  }, [classes, editForm.class_id]);

  const selectedCreateLesson = useMemo(() => {
    if (!createForm.lesson_id) return null;
    return lessons.find((lesson) => String(lesson?.id) === String(createForm.lesson_id)) || null;
  }, [lessons, createForm.lesson_id]);

  const isTestScoring = detail.exam?.scoring_mode === 'test';
  const isDevelopmentScoring = detail.exam?.scoring_mode === 'development';

  const hasAnyResultValue = useMemo(() => {
    return (detail.rows || []).some((row) => hasResultEntry(resultEntryValue(row, detail.exam?.scoring_mode), row?.result_status));
  }, [detail.exam?.scoring_mode, detail.rows]);

  const editHasResults = useMemo(() => {
    if (!editExam) return false;
    if (String(detail.exam?.id) === String(editExam.id)) return hasAnyResultValue;
    return Number(editExam.completed_count || 0) > 0;
  }, [detail.exam?.id, editExam, hasAnyResultValue]);

  const rowMap = useMemo(() => {
    const map = new Map();
    (detail.rows || []).forEach((row) => map.set(row.student_id, row));
    return map;
  }, [detail.rows]);

  const draftRows = useMemo(() => {
    return (detail.rows || []).map((row) => {
      const override = Object.prototype.hasOwnProperty.call(edits, row.student_id)
        ? edits[row.student_id]
        : normalizeResultStatus(row?.result_status) === RESULT_STATUS_ABSENT
          ? ABSENT_RESULT_TOKEN
          : resultEntryValue(row, detail.exam?.scoring_mode);
      return {
        ...row,
        draftValue: override ?? '',
      };
    });
  }, [detail.exam?.scoring_mode, detail.rows, edits]);

  const sortedDraftRows = useMemo(() => {
    return [...draftRows].sort((left, right) => {
      const sectionOrder = { pending: 0, absent: 1, completed: 2 };
      // Keep the list stable while the teacher is typing. A row changes group
      // only after its result is saved and the detail data is refreshed.
      const leftSection = assessmentResultSection(
        resultEntryValue(left, detail.exam?.scoring_mode),
        left.result_status,
      );
      const rightSection = assessmentResultSection(
        resultEntryValue(right, detail.exam?.scoring_mode),
        right.result_status,
      );
      if (leftSection !== rightSection) {
        return sectionOrder[leftSection] - sectionOrder[rightSection];
      }
      return String(left.student_name || '').localeCompare(String(right.student_name || ''), 'tr-TR');
    });
  }, [detail.exam?.scoring_mode, draftRows]);

  const hasChanges = useMemo(() => Object.keys(edits).length > 0, [edits]);

  const progressStats = useMemo(() => {
    const totalCount = draftRows.length;
    const enteredCount = draftRows.filter((row) => hasResultEntry(row.draftValue)).length;
    const absentCount = draftRows.filter((row) => isAbsentDraftValue(row.draftValue)).length;
    const evaluatedCount = enteredCount - absentCount;
    const missingCount = Math.max(totalCount - enteredCount, 0);
    const notifiedCount = (detail.rows || []).filter((row) => row?.notified).length;
    const draftCount = Object.keys(edits).length;
    const completionPercent = totalCount ? Math.round((enteredCount / totalCount) * 100) : 0;

    return {
      totalCount,
      enteredCount,
      absentCount,
      evaluatedCount,
      missingCount,
      notifiedCount,
      draftCount,
      completionPercent,
    };
  }, [detail.rows, draftRows, edits]);

  const loadClasses = useCallback(async () => {
    if (!schoolId) return;
    try {
      const res = await fetchJson(apis.classes(schoolId));
      if (res.ok) {
        const data = await res.json();
        const items = Array.isArray(data)
          ? data
              .map((c) => {
                if (!c) return null;
                if (typeof c === 'string') return { id: c, name: c, grade_level: null };
                return {
                  id: c?.id ?? c?.name,
                  name: c?.name ?? String(c?.id ?? ''),
                  grade_level: c?.grade_level ?? null,
                };
              })
              .filter(Boolean)
          : [];
        items.sort((a, b) => String(a?.name).localeCompare(String(b?.name), 'tr-TR'));
        setClasses(items);
      }
    } catch (err) {
      console.warn('classes load failed', err);
    }
  }, [apis, schoolId]);

  const loadLessons = useCallback(async () => {
    if (!schoolId) return;
    try {
      const res = await fetchJson(apis.lessons(schoolId));
      if (res.ok) {
        const data = await res.json();
        const items = Array.isArray(data)
          ? data
              .map((lesson) => {
                if (!lesson) return null;
                if (typeof lesson === 'string') return { id: lesson, name: lesson, code: lesson };
                const id = lesson?.id ?? lesson?.name ?? lesson?.code;
                const name = String(lesson?.name || lesson?.code || '').trim();
                const code = String(lesson?.code || '').trim();
                return name ? { id, name, code } : null;
              })
              .filter(Boolean)
          : [];
        items.sort((a, b) => String(a?.name).localeCompare(String(b?.name), 'tr-TR'));
        setLessons(items);
      }
    } catch (err) {
      console.warn('lessons load failed', err);
    }
  }, [apis, schoolId]);

  const loadExams = useCallback(
    async (focusExamId = null) => {
      if (!schoolId) return;
      setLoadingList(true);
      setListError('');
      try {
        const res = await fetchJson(apis.list(schoolId));
        if (!res.ok) throw new Error('list_failed');
        const data = await res.json();
        const items = (data || []).map(decorateExam);
        setExams(items);
        setSelectedExam((prev) => {
          if (focusExamId) {
            return items.find((it) => String(it.id) === String(focusExamId)) || prev;
          }
          if (prev) {
            const refreshed = items.find((it) => String(it.id) === String(prev.id));
            return refreshed || prev;
          }
          return prev;
        });
      } catch (err) {
        console.error(err);
        setListError('Ders değerlendirmeleri yüklenemedi. Tekrar deneyin.');
        onError && onError('Sınav listesi yüklenemedi');
      } finally {
        setLoadingList(false);
      }
    },
    [apis, schoolId, onError]
  );

  const loadDetail = useCallback(
    async (exam) => {
      if (!exam || !schoolId) return;
      setLoadingDetail(true);
      try {
        const res = await fetchJson(apis.detail(schoolId, exam.id));
        if (!res.ok) throw new Error('detail_failed');
        const data = await res.json();
        setDetail({ exam: data.exam, rows: data.rows || [] });
        setEdits({});
        setResultErrors({});
        setLocalAlert('');
      } catch (err) {
        console.error(err);
        onError && onError('Sınav detayı yüklenemedi');
        setDetail({ exam: exam, rows: [] });
        setResultErrors({});
      } finally {
        setLoadingDetail(false);
      }
    },
    [apis, schoolId, onError]
  );

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    loadLessons();
  }, [loadLessons]);

  useEffect(() => {
    loadExams();
  }, [loadExams]);

  useEffect(() => {
    if (selectedExam) {
      loadDetail(selectedExam);
    }
  }, [selectedExam, loadDetail]);

  const openAssessmentDetail = (exam) => {
    if (hasChanges && String(selectedExam?.id) !== String(exam?.id)) {
      onError && onError('Başka bir değerlendirmeye geçmeden önce değişiklikleri kaydedin.');
      return;
    }
    if (String(selectedExam?.id) !== String(exam?.id)) {
      setDetail({ exam, rows: [] });
      setEdits({});
      setResultErrors({});
      setLocalAlert('');
    }
    setSelectedExam(exam);
  };

  const closeAssessmentDetail = () => {
    if (hasChanges) {
      onError && onError('Paneli kapatmadan önce değişiklikleri kaydedin.');
      return;
    }
    setSelectedExam(null);
    setDetail({ exam: null, rows: [] });
    setEdits({});
    setResultErrors({});
    setLocalAlert('');
  };

  const handleResultChange = (studentId, value) => {
    const row = rowMap.get(studentId);
    const original = normalizeResultStatus(row?.result_status) === RESULT_STATUS_ABSENT
      ? ABSENT_RESULT_TOKEN
      : String(resultEntryValue(row, detail.exam?.scoring_mode)).trim();
    const next = String(value ?? '');
    setEdits((prev) => {
      const copy = { ...prev };
      if (next.trim() === original) {
        delete copy[studentId];
      } else {
        copy[studentId] = value;
      }
      return copy;
    });
    const validationError = resultEntryValidationError(value, detail.exam?.scoring_mode, detail.exam?.total_questions);
    setResultErrors((prev) => {
      const copy = { ...prev };
      if (validationError) copy[studentId] = validationError;
      else delete copy[studentId];
      return copy;
    });
  };

  const handleAbsentToggle = (studentId) => {
    const row = rowMap.get(studentId);
    const original = normalizeResultStatus(row?.result_status) === RESULT_STATUS_ABSENT
      ? ABSENT_RESULT_TOKEN
      : String(resultEntryValue(row, detail.exam?.scoring_mode)).trim();

    setEdits((prev) => {
      const current = Object.prototype.hasOwnProperty.call(prev, studentId)
        ? String(prev[studentId] ?? '')
        : original;
      const next = current === ABSENT_RESULT_TOKEN ? '' : ABSENT_RESULT_TOKEN;
      const copy = { ...prev };
      if (next === original) {
        delete copy[studentId];
      } else {
        copy[studentId] = next;
      }
      return copy;
    });
    setResultErrors((prev) => {
      const copy = { ...prev };
      delete copy[studentId];
      return copy;
    });
  };

  const handleScoreKeyDown = (event, studentId) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    const currentIndex = sortedDraftRows.findIndex((row) => String(row.student_id) === String(studentId));
    if (currentIndex < 0) return;

    const nextRow = sortedDraftRows[currentIndex + 1];
    if (!nextRow) return;

    const nextInput = scoreInputRefs.current[nextRow.student_id];
    if (nextInput && typeof nextInput.focus === 'function') {
      nextInput.focus();
      if (typeof nextInput.select === 'function') {
        nextInput.select();
      }
    }
  };

  const handleSave = async () => {
    if (!selectedExam || !schoolId) return;
    const validationErrors = {};
    draftRows.forEach((row) => {
      const value = Object.prototype.hasOwnProperty.call(edits, row.student_id)
        ? edits[row.student_id]
        : normalizeResultStatus(row?.result_status) === RESULT_STATUS_ABSENT
          ? ABSENT_RESULT_TOKEN
          : resultEntryValue(row, detail.exam?.scoring_mode);
      const error = resultEntryValidationError(value, detail.exam?.scoring_mode, detail.exam?.total_questions);
      if (error) validationErrors[row.student_id] = error;
    });
    if (Object.keys(validationErrors).length) {
      setResultErrors(validationErrors);
      setLocalAlert('Hatalı sonuçlar var. İşaretlenen alanları düzeltin.');
      const firstInvalidStudentId = Object.keys(validationErrors)[0];
      window.setTimeout(() => scoreInputRefs.current[firstInvalidStudentId]?.focus(), 0);
      return;
    }
    setSaving(true);
    try {
      const payloadRows = draftRows.map((row) => {
        const override = Object.prototype.hasOwnProperty.call(edits, row.student_id)
          ? edits[row.student_id]
          : normalizeResultStatus(row?.result_status) === RESULT_STATUS_ABSENT
            ? ABSENT_RESULT_TOKEN
            : resultEntryValue(row, detail.exam?.scoring_mode);
        const resultValue = typeof override === 'string' ? override.trim() : override;
        const isAbsent = resultValue === ABSENT_RESULT_TOKEN;
        const normalized = resultValue === '' || isAbsent ? null : resultValue;
        const payload = {
          student_id: row.student_id,
          result_status: isAbsent ? RESULT_STATUS_ABSENT : null,
        };
        if (isTestScoring) {
          payload.correct_count = normalized;
        } else if (isDevelopmentScoring) {
          payload.development_level = normalized;
        } else {
          payload.result_value = normalized;
        }
        return payload;
      });
      const res = await fetchJson(apis.save(schoolId, selectedExam.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: payloadRows }),
      });
      if (!res.ok) throw new Error('save_failed');
      const data = await res.json();
      setDetail({ exam: data.exam, rows: data.rows || [] });
      const completedCount = (data.rows || []).filter((row) => (
        hasResultEntry(resultEntryValue(row, data.exam?.scoring_mode), row?.result_status)
      )).length;
      setExams((current) => current.map((exam) => (
        String(exam.id) === String(selectedExam.id)
          ? { ...exam, ...data.exam, student_count: (data.rows || []).length, completed_count: completedCount }
          : exam
      )));
      setEdits({});
      setResultErrors({});
      setLocalAlert('Sonuçlar kaydedildi');
      onSuccess && onSuccess('Sonuçlar kaydedildi');
    } catch (err) {
      console.error(err);
      onError && onError('Sonuçlar kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!selectedExam || !schoolId) return;
    setSending(true);
    try {
      const res = await fetchJson(apis.send(schoolId, selectedExam.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('send_failed');
      const data = await res.json();
      setLocalAlert(`Kuyruğa eklendi • ${data.queued || 0} mesaj`);
      onSuccess && onSuccess('Sonuç bildirimleri kuyruğa eklendi');
      await loadDetail(selectedExam);
    } catch (err) {
      console.error(err);
      onError && onError('Mesajlar gönderilemedi');
    } finally {
      setSending(false);
    }
  };

  const sendPlanForExam = async (examId) => {
    const res = await fetchJson(apis.sendPlan(schoolId, examId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!res.ok) throw new Error('send_plan_failed');
    return res.json();
  };

  const resetCreateState = () => {
    setCreateForm(emptyCreateForm);
    setCreateErrors({});
    setCreatedExam(null);
  };

  const hideCreate = () => {
    setShowCreate(false);
    resetCreateState();
  };

  const toggleCreate = () => {
    if (showCreate) {
      closeCreate();
      return;
    }
    resetCreateState();
    setShowCreate(true);
  };

  const closeCreate = () => {
    if (creating || sendingPlan) return;
    hideCreate();
  };

  const openEdit = (exam = selectedExam) => {
    if (!exam) return;
    setEditErrors({});
    setEditExam(exam);
    setEditForm({
      title: exam.title || '',
      exam_date: exam.exam_date || '',
      class_id: exam.class_id ?? null,
      grade_level: exam.grade_level ?? null,
      lesson_id: exam.lesson_id ?? null,
      scoring_mode: exam.scoring_mode || 'manual',
      total_questions: exam.total_questions ?? '',
      notes: exam.notes || '',
      reminder_enabled: exam.reminder_enabled !== false,
    });
    setShowEdit(true);
  };

  const closeEdit = () => {
    if (editing) return;
    setShowEdit(false);
    setEditExam(null);
    setEditErrors({});
  };

  const handleUpdateExam = async () => {
    if (!editExam || !schoolId) return;
    const nextErrors = {};
    if (!editForm.title?.trim()) {
      nextErrors.title = 'Başlık gerekli';
    }
    if (!String(editForm.exam_date || '').trim()) {
      nextErrors.exam_date = 'Sınav tarihi gerekli';
    }
    if (!isLegacyGradeAssessment && !editForm.class_id) {
      nextErrors.class_id = 'Şube seçin';
    }
    if (!editForm.lesson_id) {
      nextErrors.lesson_id = 'Ders seçin';
    }
    if (editForm.scoring_mode === 'test' && Number(editForm.total_questions) < 1) {
      nextErrors.total_questions = 'Toplam soru sayısı en az 1 olmalı';
    }
    setEditErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      onError && onError(Object.values(nextErrors)[0]);
      return;
    }

    setEditing(true);
    try {
      const payload = {
        title: editForm.title,
        exam_date: editForm.exam_date || null,
        notes: editForm.notes || null,
        class_id: isLegacyGradeAssessment ? null : editForm.class_id,
        grade_level: isLegacyGradeAssessment ? editExam.grade_level : null,
        lesson_id: editForm.lesson_id ?? null,
        scoring_mode: editForm.scoring_mode,
        total_questions: editForm.scoring_mode === 'test' ? Number(editForm.total_questions) : null,
        reminder_enabled: !!editForm.reminder_enabled,
      };
      const res = await fetchJson(apis.update(schoolId, editExam.id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('update_failed');
      const updated = await res.json();
      setShowEdit(false);
      setEditExam(null);
      await loadExams(String(selectedExam?.id) === String(updated.id) ? updated.id : null);
      onSuccess && onSuccess('Sınav güncellendi');
    } catch (err) {
      console.error(err);
      onError && onError('Sınav güncellenemedi');
    } finally {
      setEditing(false);
    }
  };

  const openDelete = (exam = selectedExam) => {
    if (!exam) return;
    setDeleteExam(exam);
    setShowDelete(true);
  };

  const closeDelete = () => {
    if (deleting) return;
    setShowDelete(false);
    setDeleteExam(null);
  };

  const handleDeleteExam = async () => {
    if (!deleteExam || !schoolId) return;
    const deletedExam = deleteExam;
    setDeleting(true);
    try {
      const res = await fetchJson(apis.delete(schoolId, deletedExam.id), { method: 'DELETE' });
      if (!res.ok) throw new Error('delete_failed');
      setShowDelete(false);
      setDeleteExam(null);
      if (String(selectedExam?.id) === String(deletedExam.id)) {
        setSelectedExam(null);
        setDetail({ rows: [], exam: null });
        setEdits({});
      }
      await loadExams();
      onSuccess && onSuccess('Sınav silindi');
    } catch (err) {
      console.error(err);
      onError && onError('Sınav silinemedi');
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateExam = async () => {
    const nextErrors = {};
    if (!createForm.title?.trim()) {
      nextErrors.title = 'Başlık gerekli';
    }
    if (!String(createForm.exam_date || '').trim()) {
      nextErrors.exam_date = 'Sınav tarihi gerekli';
    }
    if (!selectedCreateClass) {
      nextErrors.class_id = 'Şube seçin';
    }
    if (!createForm.lesson_id) {
      nextErrors.lesson_id = 'Ders seçin';
    }
    if (createForm.scoring_mode === 'test' && Number(createForm.total_questions) < 1) {
      nextErrors.total_questions = 'Toplam soru sayısı en az 1 olmalı';
    }
    setCreateErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      onError && onError(Object.values(nextErrors)[0]);
      return;
    }
    setCreating(true);
    try {
      const payload = {
        title: createForm.title,
        exam_date: createForm.exam_date,
        notes: createForm.notes,
        class_id: createClassId,
        grade_level: null,
        lesson_id: createForm.lesson_id ?? null,
        scoring_mode: createForm.scoring_mode,
        total_questions: createForm.scoring_mode === 'test' ? Number(createForm.total_questions) : null,
        reminder_enabled: !!createForm.reminder_enabled,
      };
      const res = await fetchJson(apis.create(schoolId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('create_failed');
      const exam = await res.json();
      const scopeLabel = selectedCreateClass?.name || '-';
      await loadExams(exam.id);
      setCreatedExam({
        id: exam.id,
        title: exam.title || createForm.title,
        exam_date: exam.exam_date || createForm.exam_date || '',
        lesson_name: exam.lesson_name || selectedCreateLesson?.name || null,
        scoring_mode: exam.scoring_mode || createForm.scoring_mode,
        total_questions: exam.total_questions ?? (createForm.scoring_mode === 'test' ? Number(createForm.total_questions) : null),
        scope_label: scopeLabel,
        reminder_enabled: !!createForm.reminder_enabled,
      });
      setLocalAlert('Yeni sınav oluşturuldu');
      onSuccess && onSuccess('Yeni sınav oluşturuldu');
    } catch (err) {
      console.error(err);
      onError && onError('Sınav oluşturulamadı');
    } finally {
      setCreating(false);
    }
  };

  const handleSendCreatedPlan = async () => {
    if (!createdExam || !schoolId) return;
    setSendingPlan(true);
    try {
      const stats = await sendPlanForExam(createdExam.id);
      setLocalAlert(`Sınav oluşturuldu ve plan kuyruğa eklendi • ${formatSendStats(stats)}`);
      onSuccess && onSuccess('Yeni sınav oluşturuldu ve plan bildirimleri kuyruğa eklendi');
      await loadExams(createdExam.id);
      hideCreate();
    } catch (err) {
      console.error(err);
      onError && onError('Plan mesajları gönderilemedi');
    } finally {
      setSendingPlan(false);
    }
  };

  const disableSend =
    !detail.rows?.some((row) => hasResultEntry(resultEntryValue(row, detail.exam?.scoring_mode), row.result_status)) ||
    hasChanges ||
    sending ||
    sendingPlan;

  const disableListActions = hasChanges || saving || sending || sendingPlan;

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={trLocale}
      localeText={trTR.components.MuiLocalizationProvider.defaultProps.localeText}
    >
    <Box data-testid="course-assessment-typography" sx={{ minWidth: 0, fontSynthesis: 'none' }}>
      <Stack spacing={3}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ minWidth: 0, flex: '1 1 240px' }}>
            <Typography component="h1" sx={{ ...teacherTypography.pageTitle, color: unifiedUI.ink, overflowWrap: 'anywhere' }}>
              Ders Değerlendirmeleri
            </Typography>
            <Typography sx={{ ...teacherTypography.supportingText, mt: 0.75 }}>
              Değerlendirmeleri planlayın, öğrenci sonuçlarını girin ve velilerle paylaşın.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Yenile">
              <span><IconButton aria-label="Değerlendirmeleri yenile" onClick={() => {
                loadExams();
                if (selectedExam) loadDetail(selectedExam);
              }} disabled={loadingList || loadingDetail || disableListActions}
                sx={{ minWidth: 44, minHeight: 44 }}><RefreshIcon /></IconButton></span>
            </Tooltip>
            <Button variant="contained" onClick={toggleCreate} disableRipple sx={unifiedUI.mainAction}>
              Yeni değerlendirme
            </Button>
          </Stack>
        </Box>
        {listError && <Alert severity="error" data-testid="unified-assessment-load-error"
          action={<Button color="inherit" disabled={loadingList} onClick={() => loadExams()} sx={{ minHeight: 44 }}>Tekrar dene</Button>}>
          {listError}
        </Alert>}
              {loadingList && !exams.length ? (
                <Paper variant="outlined" sx={{ py: 5 }}>
                  <Stack
                    role="status"
                    aria-live="polite"
                    alignItems="center"
                    spacing={1.5}
                    color="text.secondary"
                  >
                    <CircularProgress size={28} aria-label="Ders değerlendirmeleri yükleniyor" />
                    <Typography variant="body2">Ders değerlendirmeleri yükleniyor…</Typography>
                  </Stack>
                </Paper>
              ) : !exams.length && listError ? null : !exams.length ? (
                <Box sx={{ ...teacherTypography.supportingText, fontSize: 14, py: 4, textAlign: 'center' }}>
                    <Typography variant="body2">Henüz ders değerlendirmesi oluşturulmadı. Yukarıdaki “Yeni değerlendirme” butonuyla başlayın.</Typography>
                </Box>
              ) : (
                <>
                <CourseAssessmentList exams={exams} selectedExamId={selectedExam?.id}
                  disabled={disableListActions} onOpen={openAssessmentDetail}
                  renderMenu={exam => <AssessmentActionMenu exam={exam} onEdit={openEdit} onDelete={openDelete} disabled={disableListActions} />} />

                <Drawer
                  anchor="right"
                  open={Boolean(selectedExam)}
                  onClose={closeAssessmentDetail}
                  PaperProps={{
                    sx: {
                      width: { xs: '100%', sm: 720, md: 840 },
                      maxWidth: '100vw',
                      bgcolor: unifiedUI.surfaceSubtle,
                      '& .MuiChip-root': { fontSize: 13 },
                      '& .MuiChip-colorWarning': { color: '#825020', bgcolor: '#FFF4E7', borderColor: '#CA9B61' },
                      '& .MuiChip-colorSuccess': { color: '#176846', bgcolor: '#EDF9F2', borderColor: '#9FCEB6' },
                      '& .MuiChip-colorError': { color: '#9F2525', bgcolor: '#FDEFEF', borderColor: '#E2ADAD' },
                      '& .MuiChip-clickable:focus-visible': { outline: `3px solid ${unifiedUI.purple}`, outlineOffset: 2 },
                    },
                  }}
                >
                  <Box sx={{ minHeight: '100%' }}>
                    <Box
                      sx={{
                        position: 'sticky',
                        '@media (max-height:500px)': { position: 'static' },
                        top: 0,
                        zIndex: 2,
                        px: { xs: 2, sm: 2.5 },
                        py: 2,
                        bgcolor: 'background.paper',
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="overline" sx={{ ...teacherTypography.metricLabel, letterSpacing: 0.5 }}>Ders değerlendirmesi</Typography>
                          <Typography variant="h6" sx={{ ...teacherTypography.sectionTitle, overflowWrap: 'anywhere' }}>{detail.exam?.title || selectedExam?.title}</Typography>
                          <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14 }}>
                            {[detail.exam?.exam_date ? humanDate(detail.exam.exam_date) : null,
                              detail.exam?.grade_level ? `${detail.exam.grade_level}. Sınıf` : detail.exam?.class_name,
                              detail.exam?.lesson_name,
                              detail.exam?.scoring_mode === 'test'
                                ? `${detail.exam?.total_questions || 0} soruluk test`
                                : resultEntryLabel(detail.exam?.scoring_mode)].filter(Boolean).join(' · ')}
                          </Typography>
                        </Box>
                        <IconButton aria-label="Değerlendirmeyi kapat" onClick={closeAssessmentDetail} sx={{ minHeight: 44, minWidth: 44 }}>
                          <CloseIcon />
                        </IconButton>
                      </Stack>
                    </Box>
                    <Box sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ py: 0.5 }}>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          <Chip
                            size="small"
                            color="success"
                            variant="outlined"
                            label={`${progressStats.enteredCount}/${progressStats.totalCount} tamamlandı`}
                          />
                          <Chip size="small" variant="outlined" label={`${progressStats.evaluatedCount} değerlendirildi`} />
                          <Chip size="small" variant="outlined" label={`${progressStats.absentCount} katılmadı`} />
                          <Chip
                            size="small"
                            color={progressStats.missingCount > 0 ? 'warning' : 'default'}
                            variant="outlined"
                            label={`${progressStats.missingCount} bekleyen`}
                          />
                          <NotificationStatusChip status={detail.exam?.result_message_status} />
                          {hasChanges && (
                            <Chip size="small" label={`${progressStats.draftCount} kaydedilmemiş`} color="warning" />
                          )}
                        </Stack>
                      </Box>
                      {localAlert && (
                        <Alert severity={localAlert.startsWith('Hatalı') ? 'error' : 'success'} variant="outlined">
                          {localAlert}
                        </Alert>
                      )}

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {!selectedExam ? (
                      <Alert severity="info">Sonuç girmek için ders değerlendirmelerinden birine tıklayın.</Alert>
                    ) : loadingDetail ? (
                      <Box sx={{ py: 6, textAlign: 'center' }}>
                        <CircularProgress size={32} />
                      </Box>
                    ) : !detail.rows?.length ? (
                      <Alert severity="info">Seçilen sınav için öğrenci bulunamadı.</Alert>
                    ) : (
                      <Stack spacing={1.5}>
                        <Box sx={{ py: 1 }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                            <Box>
                              <Typography variant="subtitle2" sx={teacherTypography.sectionTitle}>Öğrenci Sonuçları</Typography>
                              <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14 }}>
                                {isDevelopmentScoring ? 'Her öğrenci için gelişim düzeyini seçin.' : `Her öğrenci için satırdaki ${isTestScoring ? 'doğru sayısını' : '100 üzerinden puanı'} girin.`} Eksik sonuçlar üstte gösterilir.
                              </Typography>
                              <Typography variant="caption" sx={{ ...teacherTypography.metadata, display: 'block', mt: 0.5 }}>
                                {isDevelopmentScoring ? 'Seçimi değiştirebilir veya temizleyebilirsiniz. Tab ile ilerleyip Space ile seçebilirsiniz.' : "İpucu: Sonucu yazıp Enter'a bastığınızda bir sonraki öğrenciye geçebilirsiniz."}
                              </Typography>
                            </Box>
                            <Stack spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
                              <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  startIcon={<SaveIcon />}
                                  disabled={!hasChanges || saving}
                                  onClick={handleSave}
                                  size="small"
                                  sx={{ flex: { xs: 1, sm: 'initial' }, minHeight: 44 }}
                                >
                                  Sonuçları Kaydet
                                </Button>
                                <Button
                                  variant="contained"
                                  color="success"
                                  startIcon={<WhatsAppIcon />}
                                  disabled={disableSend}
                                  onClick={handleSend}
                                  size="small"
                                  sx={{ flex: { xs: 1, sm: 'initial' }, minHeight: 44 }}
                                >
                                  Sonuçları Gönder
                                </Button>
                              </Stack>
                            </Stack>
                          </Stack>
                        </Box>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(0, 1fr)',
                            gap: 0.75,
                          }}
                        >
                          {sortedDraftRows.map((row, index) => {
                            const value = Object.prototype.hasOwnProperty.call(edits, row.student_id)
                              ? edits[row.student_id]
                              : row.draftValue;
                            const isAbsent = isAbsentDraftValue(value);
                            const hasResult = hasResultEntry(value);
                            const section = assessmentResultSection(
                              resultEntryValue(row, detail.exam?.scoring_mode),
                              row.result_status,
                            );
                            const previous = sortedDraftRows[index - 1];
                            const previousSection = assessmentResultSection(
                              resultEntryValue(previous, detail.exam?.scoring_mode),
                              previous?.result_status,
                            );
                            const showSectionHeading = index === 0 || section !== previousSection;
                            const sectionLabel = section === 'pending'
                              ? 'Sonuç bekleyenler'
                              : section === 'absent'
                                ? 'Girmedi olarak işaretlenenler'
                                : 'Değerlendirilenler';
                            const enteredCorrect = Number(value);
                            const totalQuestions = Number(detail.exam?.total_questions || 0);
                            const estimatedScore = isTestScoring && !isAbsent && hasScoreValue(value) && totalQuestions > 0 && Number.isFinite(enteredCorrect)
                              ? Math.round((enteredCorrect / totalQuestions) * 100)
                              : null;
                            const validationError = resultErrors[row.student_id];
                            const hasValidResult = hasResult && !resultEntryValidationError(value, detail.exam?.scoring_mode, totalQuestions);
                            return (
                              <React.Fragment key={row.student_id}>
                                {showSectionHeading && (
                                  <Typography
                                    variant="overline"
                                    sx={{ ...teacherTypography.tableHeader, mt: index === 0 ? 0 : 1.25, px: 0.5, letterSpacing: 0.45 }}
                                  >
                                    {sectionLabel}
                                  </Typography>
                                )}
                              <StudentResultEntryCard
                                student={row}
                                scoringMode={detail.exam?.scoring_mode}
                                value={value}
                                isAbsent={isAbsent}
                                isDirty={Object.prototype.hasOwnProperty.call(edits, row.student_id)}
                                savedSection={section}
                                hasValidResult={hasValidResult}
                                validationError={validationError}
                                totalQuestions={detail.exam?.total_questions}
                                estimatedScore={estimatedScore}
                                disabled={saving}
                                onValueChange={(next) => handleResultChange(row.student_id, next)}
                                onAbsentToggle={() => handleAbsentToggle(row.student_id)}
                                onKeyDown={(event) => handleScoreKeyDown(event, row.student_id)}
                                inputRef={(element) => {
                                  if (element) scoreInputRefs.current[row.student_id] = element;
                                  else delete scoreInputRefs.current[row.student_id];
                                }}
                              />
                              </React.Fragment>
                            );
                          })}
                        </Box>
                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1, width: '100%' }}>
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<SaveIcon />}
                            disabled={!hasChanges || saving}
                            onClick={handleSave}
                            size="small"
                            sx={{ flex: { xs: 1, sm: 'initial' }, minHeight: 44 }}
                          >
                            Sonuçları Kaydet
                          </Button>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<WhatsAppIcon />}
                            disabled={disableSend}
                            onClick={handleSend}
                            size="small"
                            sx={{ flex: { xs: 1, sm: 'initial' }, minHeight: 44 }}
                          >
                            Sonuçları Gönder
                          </Button>
                        </Stack>
                      </Stack>
                    )}
                  </Grid>
                </Grid>
                    </Box>
                  </Box>
                </Drawer>
                </>
              )}
      </Stack>

      <Dialog
        open={showCreate}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') closeCreate();
        }}
        disableEscapeKeyDown
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{createdExam ? '2. Adım: Planı Gönder' : 'Yeni Ders Değerlendirmesi'}</DialogTitle>
        <DialogContent>
          {createdExam ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="success" variant="outlined">
                Ders değerlendirmesi oluşturuldu. Şimdi plan mesajını velilere gönderme adımındasınız.
              </Alert>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" fontWeight={600}>{createdExam.title}</Typography>
                  <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14 }}>
                    Sınav Tarihi: {createdExam.exam_date ? humanDate(createdExam.exam_date) : '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14 }}>
                    Kapsam: {createdExam.scope_label}
                  </Typography>
                  {createdExam.lesson_name && (
                    <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14 }}>
                      Ders: {createdExam.lesson_name}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14 }}>
                    Hatırlatma: {createdExam.reminder_enabled ? 'Açık' : 'Kapalı'}
                  </Typography>
                </Stack>
              </Paper>
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Değerlendirme başlığı"
                placeholder="Örn. Toplama ve Çıkarma"
                required
                fullWidth
                value={createForm.title}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setCreateForm((prev) => ({ ...prev, title: nextValue }));
                  setCreateErrors((prev) => ({ ...prev, title: nextValue.trim() ? null : prev.title }));
                }}
                error={Boolean(createErrors.title)}
                helperText={createErrors.title || 'Konuyu yazın. Örn. Toplama ve Çıkarma, Okuduğunu Anlama.'}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
                <DatePicker
                  label="Sınav Tarihi"
                  value={toPickerDate(createForm.exam_date)}
                  onChange={(value) => {
                    const nextValue = toIsoDate(value);
                    setCreateForm((prev) => ({ ...prev, exam_date: nextValue }));
                    setCreateErrors((prev) => ({ ...prev, exam_date: nextValue ? null : prev.exam_date }));
                  }}
                  format="dd.MM.yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: Boolean(createErrors.exam_date),
                      helperText: createErrors.exam_date || '',
                      sx: { minWidth: 160 },
                    },
                  }}
                />
                {classes.length === 1 && selectedCreateClass ? (
                  <Box sx={{ width: '100%', minWidth: 0 }}>
                    <Typography variant="caption">Şube</Typography>
                    <Typography sx={{ overflowWrap: 'anywhere' }}>{selectedCreateClass.name}</Typography>
                  </Box>
                ) : (
                  <Autocomplete
                    fullWidth
                    options={classes}
                    value={selectedCreateClass}
                    isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                    getOptionLabel={(opt) => opt?.name || ''}
                    onChange={(_, value) => {
                      setCreateForm((prev) => ({ ...prev, class_id: value?.id ?? null }));
                      setCreateErrors((prev) => ({ ...prev, class_id: value?.id ? null : prev.class_id }));
                    }}
                    renderOption={({ key, ...props }, option) => <li key={key} {...props} style={{ minHeight: 44 }}>{option.name}</li>}
                    renderInput={(params) => <TextField {...params} label="Şube" required error={Boolean(createErrors.class_id)} helperText={createErrors.class_id || ''} />}
                  />
                )}
              </Stack>
              <TextField
                id="create-assessment-lesson"
                label="Ders"
                select
                required
                fullWidth
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                value={createForm.lesson_id ?? ''}
                onChange={(event) => {
                  const lessonId = lessons.find((lesson) => String(lesson.id) === event.target.value)?.id ?? null;
                  setCreateForm((prev) => ({ ...prev, lesson_id: lessonId }));
                  setCreateErrors((prev) => ({ ...prev, lesson_id: lessonId ? null : prev.lesson_id }));
                }}
                error={Boolean(createErrors.lesson_id)}
                helperText={createErrors.lesson_id || (lessons.length ? 'Listeden ders seçin.' : 'Seçilebilecek ders bulunamadı.')}
              >
                <option value="">Ders seçin</option>
                {lessons.map((lesson) => <option key={lesson.id} value={lesson.id}>{lesson.name}</option>)}
              </TextField>
               <AssessmentScoringSelector
                value={createForm.scoring_mode}
                onChange={(value) => {
                  setCreateForm((prev) => ({
                    ...prev,
                    scoring_mode: value,
                    total_questions: value === 'test' ? prev.total_questions : '',
                  }));
                  setCreateErrors((prev) => ({ ...prev, total_questions: null }));
                }}
              />
               {createForm.scoring_mode === 'test' && (
                 <TextField
                   label="Toplam soru sayısı"
                   type="number"
                   required
                   fullWidth
                   inputProps={{ min: 1, step: 1 }}
                   value={createForm.total_questions}
                   onChange={(event) => {
                     const nextValue = event.target.value;
                     setCreateForm((prev) => ({ ...prev, total_questions: nextValue }));
                     setCreateErrors((prev) => ({ ...prev, total_questions: Number(nextValue) >= 1 ? null : prev.total_questions }));
                   }}
                   error={Boolean(createErrors.total_questions)}
                   helperText={createErrors.total_questions || 'Öğrenciler için doğru sayısı girilir; puan otomatik hesaplanır.'}
                 />
               )}
              <TextField
                label="Not"
                fullWidth
                multiline
                minRows={3}
                value={createForm.notes}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, notes: e.target.value }))}
              />
              <FormControlLabel
                control={<Switch checked={!!createForm.reminder_enabled} onChange={(e) => setCreateForm((prev) => ({ ...prev, reminder_enabled: e.target.checked }))} />}
                label="1 gün önce hatırlatma mesajı gönder"
              />
              <Alert severity="info" variant="outlined">
                İlk adımda sınav oluşturulur. Oluşturma sonrası otomatik olarak plan gönderme adımına geçersiniz.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        {createdExam ? (
          <DialogActions>
            <Button onClick={closeCreate} disabled={sendingPlan}>Atla</Button>
            <Button variant="contained" color="success" onClick={handleSendCreatedPlan} disabled={sendingPlan}>
              Planı Gönder
            </Button>
          </DialogActions>
        ) : (
          <DialogActions>
            <Button onClick={closeCreate} disabled={creating}>Vazgeç</Button>
            <Button variant="contained" onClick={handleCreateExam} disabled={creating}>
              Oluştur
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <Dialog
        open={showEdit}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') closeEdit();
        }}
        disableEscapeKeyDown
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Ders Değerlendirmesini Düzenle</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Değerlendirme başlığı"
              placeholder="Örn. Toplama ve Çıkarma"
                required
              fullWidth
              value={editForm.title}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setEditForm((prev) => ({ ...prev, title: nextValue }));
                  setEditErrors((prev) => ({ ...prev, title: nextValue.trim() ? null : prev.title }));
                }}
                error={Boolean(editErrors.title)}
                helperText={editErrors.title || 'Konuyu yazın. Örn. Toplama ve Çıkarma, Okuduğunu Anlama.'}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
              <DatePicker
                label="Sınav Tarihi"
                value={toPickerDate(editForm.exam_date || '')}
                onChange={(value) => {
                  const nextValue = toIsoDate(value);
                  setEditForm((prev) => ({ ...prev, exam_date: nextValue }));
                  setEditErrors((prev) => ({ ...prev, exam_date: nextValue ? null : prev.exam_date }));
                }}
                format="dd.MM.yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: Boolean(editErrors.exam_date),
                    helperText: editErrors.exam_date || '',
                    sx: { minWidth: 160 },
                  },
                }}
              />
              {isLegacyGradeAssessment ? (
                <Box sx={{ width: '100%', minWidth: 0 }}>
                  <Typography variant="caption">Mevcut kapsam</Typography>
                  <Typography sx={{ overflowWrap: 'anywhere' }}>{editExam.grade_level}. Sınıf</Typography>
                  <Typography variant="body2">Eski değerlendirmenin öğrenci kapsamı korunur.</Typography>
                </Box>
              ) : classes.length === 1 && selectedEditClass ? (
                <Box sx={{ width: '100%', minWidth: 0 }}>
                  <Typography variant="caption">Şube</Typography>
                  <Typography sx={{ overflowWrap: 'anywhere' }}>{selectedEditClass.name}</Typography>
                </Box>
              ) : (
                <Autocomplete
                  fullWidth
                  options={classes}
                  value={selectedEditClass}
                  isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                  getOptionLabel={(opt) => opt?.name || ''}
                  onChange={(_, value) => {
                    setEditForm((prev) => ({ ...prev, class_id: value?.id ?? null }));
                    setEditErrors((prev) => ({ ...prev, class_id: value?.id ? null : prev.class_id }));
                  }}
                  renderOption={({ key, ...props }, option) => <li key={key} {...props} style={{ minHeight: 44 }}>{option.name}</li>}
                  renderInput={(params) => <TextField {...params} label="Şube" required error={Boolean(editErrors.class_id)} helperText={editErrors.class_id || ''} />}
                  disabled={editHasResults}
                />
              )}
            </Stack>
            <TextField
              id="edit-assessment-lesson"
              label="Ders"
              select
              required
              fullWidth
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              value={editForm.lesson_id ?? ''}
              onChange={(event) => {
                const lessonId = lessons.find((lesson) => String(lesson.id) === event.target.value)?.id ?? null;
                setEditForm((prev) => ({ ...prev, lesson_id: lessonId }));
                setEditErrors((prev) => ({ ...prev, lesson_id: lessonId ? null : prev.lesson_id }));
              }}
              error={Boolean(editErrors.lesson_id)}
              helperText={editErrors.lesson_id || (lessons.length ? 'Listeden ders seçin.' : 'Seçilebilecek ders bulunamadı.')}
            >
              <option value="">Ders seçin</option>
              {lessons.map((lesson) => <option key={lesson.id} value={lesson.id}>{lesson.name}</option>)}
            </TextField>
            <AssessmentScoringSelector
                value={editForm.scoring_mode}
                disabled={editHasResults}
                onChange={(value) => {
                  setEditForm((prev) => ({
                    ...prev,
                    scoring_mode: value,
                    total_questions: value === 'test' ? prev.total_questions : '',
                  }));
                  setEditErrors((prev) => ({ ...prev, total_questions: null }));
                }}
              />
            {editForm.scoring_mode === 'test' && (
              <TextField
                label="Toplam soru sayısı"
                type="number"
                required
                fullWidth
                inputProps={{ min: 1, step: 1 }}
                value={editForm.total_questions}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setEditForm((prev) => ({ ...prev, total_questions: nextValue }));
                  setEditErrors((prev) => ({ ...prev, total_questions: Number(nextValue) >= 1 ? null : prev.total_questions }));
                }}
                error={Boolean(editErrors.total_questions)}
                helperText={editErrors.total_questions || 'Değişiklik mevcut sonuçları yeniden hesaplar.'}
              />
            )}
            {editHasResults && (
              <Alert severity="info" variant="outlined">
                Sonuç girilmişse kapsam ve puanlama türü değiştirilemez. Testte toplam soru sayısı güncellenebilir.
              </Alert>
            )}
            <TextField
              label="Not"
              fullWidth
              multiline
              minRows={3}
              value={editForm.notes}
              onChange={(e) => setEditForm((prev) => ({ ...prev, notes: e.target.value }))}
            />
            <FormControlLabel
              control={<Switch checked={!!editForm.reminder_enabled} onChange={(e) => setEditForm((prev) => ({ ...prev, reminder_enabled: e.target.checked }))} />}
              label="1 gün önce hatırlatma mesajı gönder"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={editing}>Vazgeç</Button>
          <Button variant="contained" onClick={handleUpdateExam} disabled={editing}>
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showDelete}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') closeDelete();
        }}
        disableEscapeKeyDown
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Sınavı Sil</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14, mt: 1 }}>
            Seçili sınav ve tüm sonuçları silinecek. Bu işlem geri alınamaz.
          </Typography>
          {deleteExam && (
            <Paper variant="outlined" sx={{ p: 1.5, mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {deleteExam.title}
              </Typography>
              <Typography variant="caption" sx={teacherTypography.metadata}>
                {deleteExam.exam_date ? humanDate(deleteExam.exam_date) : '-'}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete} disabled={deleting}>Vazgeç</Button>
          <Button color="error" variant="contained" onClick={handleDeleteExam} disabled={deleting}>
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </LocalizationProvider>
  );
}
